import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useQuery, useMutation, gql } from '@apollo/client';


// ------- FUNCTIONS
import { getCookie } from '../../util.js';

// ------- QUERIES
import QUERY_CUSTOMERS from "../../graphql/queries/Customers/QueryCustomers.js";

// ------- COMPONENTS
import LandingPage from "../../components/layout/LandingPage.js";
import NewCustomer from "./NewCustomer.js";
import CsvUpload from "../../components/layout/CsvUpload.js";
import { CustomerImportModal } from "../../components/csv-import/modals";

import {
  ForeignKeyMultiSelectDropDownInput,
} from "../../inputs/index.js";

import {
  Pane,
  AddIcon,
  SideSheet,
  DocumentShareIcon,
  ImportIcon,
  Badge,
  Dialog
} from 'evergreen-ui';

const CREATE_PAYMENT_TERM = gql`
  mutation CreatePaymentTerm($publisherId: ID!, $value: String!) {
    createPaymentTerm(publisherId: $publisherId, value: $value) {
      paymentTerm {
        id
        value
      }
    }
  }
`;

const CREATE_CUSTOMER_CLASS = gql`
  mutation CreateCustomerClass($publisherId: ID!, $value: String!) {
    createCustomerClass(publisherId: $publisherId, value: $value) {
      customerClass {
        id
        value
      }
    }
  }
`;

function Customers() {
  // ------- STATES
  const location = useLocation();


  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);

  const [customers, setCustomers] = useState([]);
  const [count, setCount] = useState(0);
  const [pagecount, setPagecount] = useState(0);
  const [currpage, setCurrpage] = useState(1);

  const [newmode, setNewmode] = useState(false);
  const [csvmode, setCsvmode] = useState(false);
  const [advancedImportMode, setAdvancedImportMode] = useState(false);
  const [params, setParams] = useState({});
  const [sourcePageInfo, setSourcePageInfo] = useState(false);

  const [showPaymentTermDialog, setShowPaymentTermDialog] = useState(false);
  const [showCustomerClassDialog, setShowCustomerClassDialog] = useState(false);
  const [unknownPaymentTerms, setUnknownPaymentTerms] = useState([]);
  const [unknownCustomerClasses, setUnknownCustomerClasses] = useState([]);
  const [currentPaymentTerm, setCurrentPaymentTerm] = useState(null);
  const [currentCustomerClass, setCurrentCustomerClass] = useState(null);

  // ------- HOOKS
  const { loading, error, data, refetch: _refetch } = useQuery(QUERY_CUSTOMERS, {
    variables: { publisher: String(currentpublisher.id), page: parseInt(currpage), params: JSON.stringify(params) },
  });
  const refetch = useCallback(() => { setTimeout(() => _refetch(), 0) }, [_refetch]);

  const [createPaymentTerm] = useMutation(CREATE_PAYMENT_TERM);
  const [createCustomerClass] = useMutation(CREATE_CUSTOMER_CLASS);

  useEffect(() => {
    if (data) {
      setCustomers(data.customers);
      const csrftoken = getCookie('csrftoken');
      fetch('/count_customers/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify({ publisher: currentpublisher.id })
      })
        .then(res => res.json())
        .then(data => {
          setCount(parseInt(data.message));
          setPagecount(Math.ceil(data.message / 25));
        })
    }
  }, [data, currentpublisher.id]);

  useEffect(() => {
    if (location.state) {
      let locState = location.state;
      setSourcePageInfo(locState)
    }
  }, [location.state]);


  // ------- FUNCTIONS
  const handleRefetchCustomers = () => {
    setParams({});
    refetch();
  }

  const handleToggleNewMode = () => {
    setNewmode(!newmode);
  }

  const handleToggleCsvMode = () => {
    setCsvmode(!csvmode);
    refetch();
  }

  const handleToggleAdvancedImportMode = () => {
    setAdvancedImportMode(!advancedImportMode);
  }

  const handleAddPaymentTerm = async (paymentTerm) => {
    try {
      const { data } = await createPaymentTerm({
        variables: {
          value: paymentTerm,
          publisherId: currentpublisher.id.toString()
        }
      });

      if (data.createPaymentTerm.paymentTerm) {
        setUnknownPaymentTerms(prev => prev.filter(pt => pt !== paymentTerm));
        if (unknownPaymentTerms.length <= 1) {
          setShowPaymentTermDialog(false);
        } else {
          setCurrentPaymentTerm(unknownPaymentTerms[1]);
        }
      }
    } catch (error) {
      console.error('Error adding payment term:', error);
    }
  };

  const handleAddCustomerClass = async (customerClass) => {
    try {
      const { data } = await createCustomerClass({
        variables: {
          value: customerClass,
          publisherId: currentpublisher.id.toString()
        }
      });

      if (data.createCustomerClass.customerClass) {
        setUnknownCustomerClasses(prev => prev.filter(cc => cc !== customerClass));
        if (unknownCustomerClasses.length <= 1) {
          setShowCustomerClassDialog(false);
        } else {
          setCurrentCustomerClass(unknownCustomerClasses[1]);
        }
      }
    } catch (error) {
      console.error('Error adding customer class:', error);
    }
  };

  // ------- INPUTS

  const buttons = [
    { title: "NEW CUSTOMER", icon: AddIcon, onClick: setNewmode },
    // { title: "IMPORT CUSTOMERS", icon: DocumentShareIcon, onClick: setCsvmode },
    { title: "IMPORT CUSTOMERS", icon: ImportIcon, onClick: handleToggleAdvancedImportMode },
  ];

  // Dynamically build cols array based on whether sales by region is enabled
  const cols = [
    { title: "FIRST NAME", type: "button", flex: "2", query: "first_name" },
    { title: "LAST NAME", type: "button", flex: "2", query: "last_name" },
    { title: "COMPANY", type: "button", flex: "2", query: "company" },
    ...(publisherGlobalData.enable_sales_by_region ? [{ title: "COUNTRY", type: "default", flex: "1" }] : []),
    { title: "PAYMENT TERMS", type: "button", flex: "2", query: "payment_terms__value" },
    { title: "CUSTOMER CLASS", type: "button", flex: "2", query: "customer_class__value" },
  ]

  const searchInit = {
    all: "",
    full_name: "",
    company: "",
    out_of_business: false,
    customer_class__value: "",
    payment_terms__value: ""
  }

  const searchInputs = {
    default: { searchq: "all", dropLabel: "Default" },
    full_name: { searchq: "full_name", dropLabel: "By Customer Name" },
    company: { searchq: "company", dropLabel: "By Company Name" },
  }

  const filterInputs = {
    customer_class__value: { comp: ForeignKeyMultiSelectDropDownInput, formLabel: "CLASS", group: 1, values: publisherGlobalData.customerclasses, marginRight: 4 },
    payment_terms__value: { comp: ForeignKeyMultiSelectDropDownInput, formLabel: "TERMS", group: 1, values: publisherGlobalData.paymentterms, marginRight: 4 },
  }

  // const checkInputs = {
  //   out_of_business: { width: 200, comp: CheckboxInput, formLabel: "Out of business?", group: 2 },
  // }

  const csvFields = [
    "adminNotes",
    "company",
    "created",
    "customerClass",
    "customeremailSet",
    "customerphonenumberSet",
    "customeraddressSet",
    "firstName",
    "id",
    "lastName",
    "outOfBusiness",
    "paymentTerms",
    "country",
  ];

  const csvConfig = {
    "paymentTerms": ["value"],
    "customerClass": ["value"],
    "country": ["code", "name"],
    "primarySets": {
      "customeraddressSet": {
        "fields": ["line1", "line2", "city", "stateOrRegion", "country", "zipcode"],
        "subfields": {},
        "label": "addresses"
      },
      "customeremailSet": {
        "fields": ["value"],
        "subfields": {},
        "label": "emails"
      },
      "customerphonenumberSet": {
        "fields": ["value"],
        "subfields": {},
        "label": "phoneNumbers"
      }
    },
    "sets": {},
    "arrays": {}
  }

  // Create CSV template including country code if sales by region is enabled
  const baseTemplate = [
    [
      "first_name",
      "last_name",
      "company",
      ...(publisherGlobalData.enable_sales_by_region ? ["country_code"] : []),
      "payment_term",
      "customer_class",
      "email",
      "phone",
      "address_line1",
      "address_line2",
      "address_city",
      "address_region",
      "address_zipcode",
      "address_country",
      "admin_notes"
    ],
    [
      "Customer's first name",
      "Customer's last name",
      "Company Name",
      ...(publisherGlobalData.enable_sales_by_region ? ["ISO country code (e.g., US, GB, CA)"] : []),
      "The preferred payment term as it exists in Working Lit",
      "The customer class as it exists in Working Lit",
      "Primary email",
      "Primary phone number",
      "Street address",
      "Street address - line 2",
      "City",
      "State or Region",
      "Zipcode",
      "Country",
      "Notes about customer"
    ],
    [
      "Ella",
      "Green",
      "Main Street Gift Shop",
      ...(publisherGlobalData.enable_sales_by_region ? ["US"] : []),
      "pre-pay",
      "Consumer",
      "main-street@gift.shop",
      "777-777-7777",
      "17515 S Miles Rd",
      "",
      "Cleveland",
      "OH",
      "44128",
      "US",
      "make sure they pre-pay"
    ]
  ];

  const csvTemplate = baseTemplate;

  // Adjust CSV column widths
  const csvWidths = [
    3, // first_name
    3, // last_name
    2, // company
    ...(publisherGlobalData.enable_sales_by_region ? [1] : []), // country_code
    2, // payment_term
    2, // customer_class
    4, // email
    3, // phone
    2, // address_line1
    2, // address_line2
    2, // address_city
    2, // address_region
    2, // address_zipcode
    2, // address_country
    3  // admin_notes
  ]

  if (error) return `Error! ${error}`;

  return (
    <Pane position="absolute" padding={16} width="100%" height="100%">
      {csvmode ? (
        <>
          <CsvUpload
            handleToggleCsvMode={handleToggleCsvMode}
            template={csvTemplate}
            itemType="customer"
            tableWidth={2000}
            colWidths={csvWidths}
            onValidationError={(errors) => {
              if (errors.unknown_payment_terms) {
                setUnknownPaymentTerms(errors.unknown_payment_terms);
                setCurrentPaymentTerm(errors.unknown_payment_terms[0]);
                setShowPaymentTermDialog(true);
              }
              if (errors.unknown_customer_classes) {
                setUnknownCustomerClasses(errors.unknown_customer_classes);
                setCurrentCustomerClass(errors.unknown_customer_classes[0]);
                setShowCustomerClassDialog(true);
              }
            }}
          />

          <Dialog
            isShown={showPaymentTermDialog}
            title="Unknown Payment Term"
            onCloseComplete={() => setShowPaymentTermDialog(false)}
            confirmLabel="Yes, please add it!"
            cancelLabel="Cancel, I will fix the spreadsheet"
            onConfirm={() => handleAddPaymentTerm(currentPaymentTerm)}
            hasFooter={true}
            hasHeader={true}
            intent="none"
            width="560px"
            topOffset="80px"
            sideOffset="80px"
            confirmAppearance="dialogConfirm"
            cancelAppearance="dialogCancel"
          >
            These payment terms "{currentPaymentTerm}" were not found in your preferences. Would you like us to add them?
          </Dialog>

          <Dialog
            isShown={showCustomerClassDialog}
            title="Unknown Customer Class"
            onCloseComplete={() => setShowCustomerClassDialog(false)}
            confirmLabel="Yes, please add it!"
            cancelLabel="Cancel, I will fix the spreadsheet"
            onConfirm={() => handleAddCustomerClass(currentCustomerClass)}
            intent="none"
            confirmAppearance="dialogConfirm"
            cancelAppearance="dialogCancel"
          >
            This customer class "{currentCustomerClass}" was not found in your preferences. Would you like us to add it?
          </Dialog>
        </>
      ) : (
        <Pane>
          <SideSheet isShown={newmode} onCloseComplete={() => setNewmode(false)}>
            <NewCustomer
              handleToggleNewMode={handleToggleNewMode}
              handleRefetchCustomers={handleRefetchCustomers}
              publisher={currentpublisher.id}
            />
          </SideSheet>
          <LandingPage
            title="Customers"
            location={location}
            sourcePageInfo={sourcePageInfo}
            data={customers}
            loading={loading}
            buttons={buttons}
            count={count}
            pagecount={pagecount}
            searchInputs={searchInputs}
            filterInputs={filterInputs}
            searchInit={searchInit}
            csvConfig={csvConfig}
            csvFields={csvFields}
            csvType="Customers"
            cols={cols}
            refetch={refetch}
            setParams={setParams}
            RowComponent={CustomerRow}
            currpage={currpage}
            setCurrpage={setCurrpage}
            colWidths={["0.01"].concat(cols.map((col) => col.flex))}
          />
        </Pane >
      )}
      <CustomerImportModal isShown={advancedImportMode} onClose={handleToggleAdvancedImportMode} />
    </Pane>
  );
}

function CustomerRow({ item, allmode, handleCheck, colWidths, makeIDCell, makeTableCellArray }) {
  const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
  const [checked, setChecked] = useState(false);

  const handleSetChecked = () => {
    handleCheck(checked, item)
    setChecked(!checked);
  }

  // Get the primary address country if available
  const getPrimaryAddressCountry = () => {
    if (item.customeraddressSet && item.customeraddressSet.length > 0) {
      const primaryAddress = item.customeraddressSet.find(address => address.primary);
      if (primaryAddress && primaryAddress.country) {
        return primaryAddress.country;
      }
    }
    return '—';
  };

  // Build row cells array based on whether sales by region is enabled
  const rowCells = [
    {
      content: makeIDCell(checked, handleSetChecked, item.id), disableSelect:true
    },
    { content: item.firstName },
    { content: item.lastName},
    { content: item.company},
    ...(publisherGlobalData.enable_sales_by_region ? [
      { content: getPrimaryAddressCountry() }
    ] : []),
    { content: item.paymentTerms && item.paymentTerms.value},
    { content: <Badge color="neutral">{item.customerClass && item.customerClass.value}</Badge>
  }
  ]

  useEffect(() => {
    setChecked(allmode);
  }, [allmode]);
  const cellArray = makeTableCellArray(rowCells, colWidths, item.id)

  return cellArray
}

export default Customers;
